import React from 'react';
import { Select } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { DeleteIcon } from '@/assets/icons/icons';
import type { TimeRange } from '@/apis/types';
import {
  END_TIME_OPTIONS,
  MAX_RANGES_PER_DAY,
  START_TIME_OPTIONS,
  nextRange,
  rangesError,
} from './operatingHours';
import styles from './OperatingHours.module.scss';

interface TimeRangeListProps {
  ranges: TimeRange[];
  onChange: (ranges: TimeRange[]) => void;
  /** Used in accessible names, e.g. "Monday". */
  label: string;
}

/** Start/end selects per range, a delete icon when split, and "Add hours". */
const TimeRangeList: React.FC<TimeRangeListProps> = ({
  ranges,
  onChange,
  label,
}) => {
  const update = (index: number, patch: Partial<TimeRange>) =>
    onChange(ranges.map((r, i) => (i === index ? { ...r, ...patch } : r)));

  const next = nextRange(ranges);
  const canAdd = next !== null && ranges.length < MAX_RANGES_PER_DAY;
  const error = rangesError(ranges);
  // Ranges have no ids; rows are edited and removed by position.
  const slots = ranges.map((range, index) => ({
    range,
    index,
    key: `slot-${index}`,
  }));

  return (
    <div className={styles.rangeList}>
      {slots.map(({ range, index, key }) => (
        <div className={styles.rangeRow} key={key}>
          <Select
            variant='filled'
            className={styles.filledControl}
            value={range.start}
            options={START_TIME_OPTIONS}
            onChange={(start: string) => update(index, { start })}
            aria-label={`${label} opens`}
            showSearch
          />
          <span className={styles.rangeDash}>-</span>
          <Select
            variant='filled'
            className={styles.filledControl}
            value={range.end}
            options={END_TIME_OPTIONS}
            onChange={(end: string) => update(index, { end })}
            aria-label={`${label} closes`}
            showSearch
          />
          {ranges.length > 1 ? (
            <button
              type='button'
              className={styles.iconButton}
              onClick={() => onChange(ranges.filter((_, i) => i !== index))}
              aria-label={`Remove ${label} hours ${range.start} to ${range.end}`}
            >
              <DeleteIcon color='var(--action-icon-color)' />
            </button>
          ) : null}
        </div>
      ))}
      {error && (
        <span className={styles.rangeError} role='alert'>
          {error}
        </span>
      )}
      {canAdd && (
        <button
          type='button'
          className={styles.addHours}
          onClick={() => onChange([...ranges, next])}
          aria-label={`Add hours for ${label}`}
        >
          <PlusOutlined style={{ fontSize: 10 }} />
          Add hours
        </button>
      )}
    </div>
  );
};

export default TimeRangeList;
